import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Trophy, ArrowLeft, Share2, Download } from 'lucide-react';
import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

// Badge icons mapping
const BADGE_ICONS = {
  ghost: "👻",
  rang_badlu: "🦎",
  fastest_fingers: "⚡",
  mr_tentative: "🐢",
  asli_ameeri: "💰",
  play_now_pay_later: "🏦",
  breakfast_paglu: "🍳"
};

// Badge colors
const BADGE_COLORS = {
  ghost: "#6B7280",
  rang_badlu: "#F59E0B",
  fastest_fingers: "#3B82F6",
  mr_tentative: "#8B5CF6",
  asli_ameeri: "#10B981",
  play_now_pay_later: "#EF4444",
  breakfast_paglu: "#F97316"
};

function AwardsPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState('breakfast_paglu');
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUser, setShareUser] = useState(null);
  const [shareBadge, setShareBadge] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      setCurrentUser(res.data);
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stats/badges`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
      toast.error('Failed to load awards');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (badgeType, user) => {
    setShareBadge(badgeType);
    setShareUser(user);
    setShareDialogOpen(true);
  };

  const shareToWhatsApp = async () => {
    if (!shareBadge || !shareUser) return;
    
    const badge = stats?.badges?.[shareBadge];
    if (!badge) return;
    
    try {
      // Download the certificate image
      const response = await axios.get(
        `${API_URL}/api/stats/badge-image/${shareBadge}/${shareUser.user_id}`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      
      const imageBlob = new Blob([response.data], { type: 'image/png' });
      const imageFile = new File([imageBlob], `${shareUser.name}_${badge.name}.png`, { type: 'image/png' });
      
      // Check if Web Share API with files is supported (mobile browsers)
      if (navigator.canShare && navigator.canShare({ files: [imageFile] })) {
        await navigator.share({
          files: [imageFile],
          title: `${shareUser.name} - ${badge.name}`,
          text: `🏆 ${shareUser.name} earned the ${badge.name} badge!\n\n${badge.icon} ${badge.description}\n\n🏏 Weakenders\n${window.location.origin}/awards`
        });
        toast.success('Shared successfully!');
      } else {
        // Fallback: Download image and show WhatsApp text link
        const url = window.URL.createObjectURL(imageBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${shareUser.name}_${badge.name}.png`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.info('Certificate downloaded! You can now share it on WhatsApp.');
        
        // Open WhatsApp with text after a short delay
        setTimeout(() => {
          const message = `🏆 *${shareUser.name}* earned the *${badge.name}* badge!\n\n${badge.icon} ${badge.description}\n\n🏏 Weakenders\n${window.location.origin}/awards`;
          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
          window.open(whatsappUrl, '_blank');
        }, 500);
      }
    } catch (error) {
      console.error('Share failed:', error);
      toast.error('Failed to share. Please try downloading instead.');
    }
    
    setShareDialogOpen(false);
  };

  const downloadBadgeImage = async () => {
    if (!shareBadge || !shareUser) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/stats/badge-image/${shareBadge}/${shareUser.user_id}`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${shareBadge}_${shareUser.name}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error('Failed to download certificate');
    }
  };

  // Share All Awards for current user
  const shareAllMyAwards = async () => {
    if (!currentUser) {
      toast.error('Please log in to share your awards');
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/api/stats/all-awards-image/${currentUser.user_id}`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );

      const imageBlob = new Blob([response.data], { type: 'image/png' });
      const imageFile = new File([imageBlob], `${currentUser.name}_all_awards.png`, { type: 'image/png' });

      // Check if Web Share API with files is supported
      if (navigator.canShare && navigator.canShare({ files: [imageFile] })) {
        await navigator.share({
          files: [imageFile],
          title: `${currentUser.name}'s Awards Collection`,
          text: `🏆 Check out my awards at Weakenders!\n\n🏏 ${window.location.origin}/awards`
        });
        toast.success('Shared successfully!');
      } else {
        // Fallback: Download image
        const url = window.URL.createObjectURL(imageBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${currentUser.name}_all_awards.png`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.info('Awards image downloaded! Share it on WhatsApp.');
        
        // Open WhatsApp with text
        setTimeout(() => {
          const message = `🏆 Check out my awards at Weakenders!\n\n🏏 ${window.location.origin}/awards`;
          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
          window.open(whatsappUrl, '_blank');
        }, 500);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('You have no awards in Top 5 yet. Keep playing!');
      } else {
        console.error('Share all awards failed:', error);
        toast.error('Failed to share awards');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946]"></div>
      </div>
    );
  }

  // Show all awards, even if empty
  const badgeOptions = [
    { key: 'ghost', label: 'Ghost', category: 'Attendance' },
    { key: 'rang_badlu', label: 'Rang Badlu', category: 'Attendance' },
    { key: 'fastest_fingers', label: 'Fastest Fingers First', category: 'Attendance' },
    { key: 'mr_tentative', label: 'Mr. Tentative', category: 'Attendance' },
    { key: 'asli_ameeri', label: 'Asli Ameeri', category: 'Payment' },
    { key: 'play_now_pay_later', label: 'Play Now Pay Later', category: 'Payment' },
    { key: 'breakfast_paglu', label: 'Breakfast Paglu', category: 'Match' },
  ];

  const currentBadge = stats?.badges?.[selectedBadge];
  const currentRankings = stats?.rankings?.[selectedBadge] || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#0F172A] text-white sticky top-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Trophy className="w-6 h-6 text-amber-400" />
              <h1 className="text-xl font-bold">Player Awards</h1>
            </div>
          </div>
          <Button
            onClick={shareAllMyAwards}
            size="sm"
            className="bg-[#E63946] hover:bg-[#C62E3A] text-white"
            data-testid="share-all-awards-btn"
          >
            <Share2 className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Share My Awards</span>
            <span className="sm:hidden">Share</span>
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Award Selector - Dropdown at top */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">Select Award Category</label>
          <Select value={selectedBadge} onValueChange={setSelectedBadge}>
            <SelectTrigger className="w-full" data-testid="award-selector">
              <SelectValue>
                <span className="flex items-center gap-2">
                  <span>{BADGE_ICONS[selectedBadge]}</span>
                  <span>{currentBadge?.name}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase">Attendance</div>
              {badgeOptions.filter(b => b.category === 'Attendance').map(({ key, label }) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-2">
                    <span>{BADGE_ICONS[key]}</span>
                    <span>{label}</span>
                  </span>
                </SelectItem>
              ))}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase mt-2">Payment</div>
              {badgeOptions.filter(b => b.category === 'Payment').map(({ key, label }) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-2">
                    <span>{BADGE_ICONS[key]}</span>
                    <span>{label}</span>
                  </span>
                </SelectItem>
              ))}
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase mt-2">Match</div>
              {badgeOptions.filter(b => b.category === 'Match').map(({ key, label }) => (
                <SelectItem key={key} value={key}>
                  <span className="flex items-center gap-2">
                    <span>{BADGE_ICONS[key]}</span>
                    <span>{label}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Award Details */}
        {currentBadge && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="text-center mb-6">
              <span className="text-5xl mb-3 block">{BADGE_ICONS[selectedBadge]}</span>
              <h2 className="text-2xl font-bold text-[#0F172A]">{currentBadge.name}</h2>
              <p className="text-sm text-[#E63946] font-medium">{currentBadge.hindi}</p>
              <p className="text-slate-600 mt-2">{currentBadge.description}</p>
            </div>

            {/* Top 5 List */}
            <div className="border-t border-slate-200 pt-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Top 5</h3>
              
              {currentRankings.length > 0 ? (
                <div className="space-y-3">
                  {currentRankings.map((player, index) => (
                    <div
                      key={player.user_id}
                      className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {/* Rank - Medal icons for 1,2,3 and ribbon for 4,5 */}
                        <div className="w-8 h-8 flex items-center justify-center text-2xl">
                          {index === 0 && "🥇"}
                          {index === 1 && "🥈"}
                          {index === 2 && "🥉"}
                          {index === 3 && "🎗️"}
                          {index === 4 && "🎗️"}
                        </div>
                        
                        {/* Badge Icon as avatar */}
                        <div className="w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center text-xl">
                          {BADGE_ICONS[selectedBadge]}
                        </div>
                        
                        {/* Player Info */}
                        <div>
                          <p className="font-semibold text-[#0F172A] text-sm">{player.name}</p>
                          <p className="text-xs text-slate-500">{player.detail}</p>
                        </div>
                      </div>
                      
                      {/* Value and Share */}
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="font-bold text-base" style={{ color: BADGE_COLORS[selectedBadge] }}>
                            {player.value}
                          </p>
                        </div>
                        <button
                          onClick={() => handleShare(selectedBadge, player)}
                          className="p-1.5 hover:bg-white rounded-full transition-colors border border-slate-200"
                          title="Share Certificate"
                          data-testid={`share-btn-${index}`}
                        >
                          <Share2 className="w-4 h-4 text-slate-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <span className="text-4xl mb-3 block">{BADGE_ICONS[selectedBadge]}</span>
                  <p className="text-slate-600 font-medium">No data yet</p>
                  <p className="text-sm text-slate-500 mt-1">Play more matches to see rankings!</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Certificate
            </DialogTitle>
          </DialogHeader>
          
          {shareUser && shareBadge && stats?.badges?.[shareBadge] && (
            <div className="space-y-4">
              {/* Certificate Preview */}
              <div className="border-4 border-[#E63946]/20 rounded-lg p-4 bg-white">
                <div className="border border-[#E63946] rounded p-4 text-center relative">
                  {/* Logo top left */}
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <img src="/images/weakenders-logo.png" alt="Logo" className="w-14 h-14" />
                  </div>
                  
                  {/* Header */}
                  <p className="font-bold text-[#E63946] text-xl mt-2 tracking-wider font-serif">WEAKENDERS</p>
                  
                  <span className="text-3xl block mt-4">{BADGE_ICONS[shareBadge]}</span>
                  <p className="font-serif italic text-lg text-[#0F172A] mt-2">{stats.badges[shareBadge].name}</p>
                  <p className="text-xs text-slate-500 mt-1">This award is proudly presented to</p>
                  <p className="text-xl font-bold text-[#E63946] mt-2">{shareUser.name}</p>
                  <p className="text-xs text-slate-400 mt-3 italic">{stats.badges[shareBadge].description}</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={shareToWhatsApp}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </Button>
                <Button
                  onClick={downloadBadgeImage}
                  variant="outline"
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AwardsPage;
